import React, { useState } from 'react';
import { path, prop } from 'ramda';
import Form from 'js/components/common/Form';
import { Button } from 'js/components/common/Button';
import { Input } from 'js/components/common/Fields/Input';
import Info from 'js/components/main/PhoneVerification/Info';
import { componentService } from 'js/services/component.service';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { usePortletTranslations } from 'js/components/system/Portlet';
import { withPortlet } from 'js/components/system/Portlet/hocs/withPortlet';
import { int, length, required } from 'js/utils/Validation/validationRules';

import { fetchUserPhoneVerification, sendPhoneVerificationCode, startPhoneVerification } from './actions';
import { PHONE_VERIFICATION } from './constants';

import style from './index.scss';

export const PhoneVerificationBare = ({
    data: { mobilePhoneVerified }
}) => {
    const t = usePortletTranslations();
    const [validationConfig] = useState({
        code: [
            required({ error: t('code-required') }),
            int({ re: /^\d+$/, error: t('code-digits') }),
            length({ min: 6, max: 6, error: t('code-length') })
        ]
    });

    const casinoName = useAppSelector(prop('casinoName'));
    const cellphone = useUserSelector(path(['userDetails', 'cellphone']));
    const [isSmsSent, setSmsSentStatus] = useState(false);
    const [verified, setVerified] = useState(mobilePhoneVerified === '1');
    const [startVerificationError, setStartVerificationError] = useState('');

    const initialValues = { cellphone, code: '' };

    const startVerification = async () => {
        try {
            await startPhoneVerification();
            setStartVerificationError('');
            setSmsSentStatus(true);
        } catch (e) {
            const errorCode = path(['response', 'data', 'errorCode'], e);
            setStartVerificationError(t(`error.${errorCode}`));
        }
    };

    const onSubmit = async (values) => {
        const { code } = values;

        try {
            const response = await sendPhoneVerificationCode({ code, casinoName });
            const { data: { status } } = response;

            if (status === 'success') {
                setVerified(true);
            } else {
                return t(status);
            }

        } catch (e) {
            const errorCode = path(['response', 'data', 'errorCode'], e);
            return t(`error.${errorCode}`);
        }
    };

    const isInfoPage = verified || !cellphone;

    if (isInfoPage) {
        return <Info {...{ cellphone }} />;
    }

    return (
        <Form
            onSubmit={onSubmit}
            validateOnBlur={false}
            initialValues={initialValues}
            validationConfig={validationConfig}
            render={({ handleSubmit, submitError, submitting, pristine }) => (
                <form
                    className={style.container}
                    onSubmit={event => event.preventDefault()}
                >
                    <div className={style.title}>
                        {isSmsSent ? t('titleCode') : t('title')}
                    </div>

                    {(submitError || startVerificationError) && (
                        <ErrorMessage
                            error={submitError || startVerificationError}
                            animate={true}
                        />)}

                    <Input
                        className={style.inputPhone}
                        type="text"
                        name="cellphone"
                        disabled
                    />

                    {isSmsSent ?
                        <>
                            <Input
                                className={style.inputCode}
                                type="text"
                                name="code"
                                placeholder={t('code-placeholder')}
                                aria-label={t(['code-placeholder', 'playtech.mobile.system.input.aria-label'])}
                            />

                            <Button
                                type="button"
                                className={style.resendBtn}
                                size="small"
                                onClick={startVerification}
                            >
                                {t('resend')}
                            </Button>

                            <Button
                                type="button"
                                onClick={handleSubmit}
                                className={style.submitBtn}
                                disabled={pristine || submitting}
                            >
                                {t('submitCode')}
                            </Button>
                        </>
                        : <Button
                            type="button"
                            className={style.submitBtn}
                            onClick={startVerification}
                        >
                            {t('submit')}
                        </Button>
                    }
                </form>
            )}
        />
    );
};

export const PhoneVerification = withPortlet({
    forLoggedIn: true,
    fetch: fetchUserPhoneVerification
})(PhoneVerificationBare);

componentService.registerPageComponents({ [PHONE_VERIFICATION]: PhoneVerification });
